import { ENDPOINTS as STREAM_ENDPOINTS } from '@polyai/stream/endpoints';

import ENDPOINTS from 'api/endpoints';
import { doDelete, doGet, doPost } from 'api/request';
import { LOGGER_TYPE } from 'api/types';
import { generateNextPath } from 'lib/routeHelper';
import type {
  FunctionBuiltins,
  FunctionCall,
  FunctionExecutionRequest,
  FunctionExecutionResponse,
  FunctionsDeployResponse,
  GetFunctionsResponse,
  PostFunction,
} from './types';

export const getFunctions = (accountId: string, projectId: string) => {
  return doGet<GetFunctionsResponse>(
    generateNextPath(ENDPOINTS.FUNCTIONS, {
      accountId,
      projectId,
    }),
    LOGGER_TYPE.JUPITER,
    { version: 'v2' },
  );
};

export const getFunction = (
  accountId: string,
  projectId: string,
  functionId: string,
) => {
  return doGet<FunctionCall>(
    generateNextPath(ENDPOINTS.FUNCTION, { accountId, projectId, functionId }),
    LOGGER_TYPE.JUPITER,
    { version: 'v2' },
  );
};

export const postFunction = ({
  accountId,
  projectId,
  func,
}: {
  accountId: string;
  projectId: string;
  func: PostFunction;
}) => {
  return doPost<FunctionCall>(
    generateNextPath(ENDPOINTS.FUNCTIONS, {
      accountId,
      projectId,
    }),
    LOGGER_TYPE.JUPITER,
    func,
    { version: 'v2' },
  );
};

export const duplicateFunction = ({
  accountId,
  projectId,
  functionId,
}: {
  accountId: string;
  projectId: string;
  functionId: string;
}) => {
  return doPost<FunctionCall>(
    generateNextPath(ENDPOINTS.DUPLICATE_FUNCTION, {
      accountId,
      projectId,
      functionId,
    }),
    LOGGER_TYPE.JUPITER,
    {},
    { version: 'v2' },
  );
};

export const deleteFunction = ({
  accountId,
  projectId,
  functionId,
}: {
  accountId: string;
  projectId: string;
  functionId: string;
}) => {
  return doDelete(
    generateNextPath(ENDPOINTS.FUNCTION, { accountId, projectId, functionId }),
    LOGGER_TYPE.JUPITER,
    {},
    { version: 'v2' },
  );
};

export const executeFunction = ({
  accountId,
  projectId,
  functionId,
  body,
}: {
  accountId: string;
  projectId: string;
  functionId: string;
  body: FunctionExecutionRequest;
}) => {
  return doPost<FunctionExecutionResponse>(
    generateNextPath(ENDPOINTS.EXECUTE_FUNCTION, {
      accountId,
      projectId,
      functionId,
    }),
    LOGGER_TYPE.JUPITER,
    body,
    { version: 'v2' },
  );
};

export const deployFunctions = ({
  accountId,
  projectId,
}: {
  accountId: string;
  projectId: string;
}) => {
  return doPost<FunctionsDeployResponse>(
    generateNextPath(ENDPOINTS.DEPLOY_FUNCTIONS, { accountId, projectId }),
    LOGGER_TYPE.JUPITER,
    {},
    { version: 'v2' },
  );
};

export const getPythonVersionZip = () => {
  return doGet<Blob>('', LOGGER_TYPE.JUPITER, {
    downloadingFile: true,
    overwriteUrl: '/code-editor/stdlib.zip',
    noAuth: true,
  });
};

export const getFunctionCustomLib = () => {
  return getGlobalFunctionLib();
};

export const getGlobalFunctionLib = () => {
  return doGet<FunctionBuiltins>(
    generateNextPath(ENDPOINTS.FUNCTIONS_LIB),
    LOGGER_TYPE.JUPITER,
    {
      version: 'v2',
    },
  );
};

export const testFunction = ({
  accountId,
  projectId,
  branchId,
  functionId,
  body,
}: {
  accountId: string;
  projectId: string;
  branchId: string;
  functionId: string;
  body: FunctionExecutionRequest;
}) => {
  return doPost<FunctionExecutionResponse>(
    generateNextPath(STREAM_ENDPOINTS.BRANCH_TEST_FUNCTION, {
      accountId,
      projectId,
      branchId,
      functionId,
    }),
    LOGGER_TYPE.AGENT_STREAM,
    body,
    { prefix: 'sourcerer' },
  );
};
